/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.15.0
 * @since 1.13.0
 */

import { useCallback } from 'react';

import { useParams } from 'react-router-dom';

import { icons } from '../../../../../assets';
import { appHistoryPush } from '../../../../../modules/app/appHistory';
import { ELogBusinessEventName, ELogBusinessEventType } from '../../../../../modules/logging/loggingConstants';
import { EOnboardingComponentsId } from '../../../../../modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '../../../../../modules/role/constants/guard/guardConstants';
import { ExternalRoutePath } from '../../../../../routes/constants/externalRoutes';
import { CLIENT_ID, IRouteParams, RoutePath } from '../../../../../routes/constants/routesConstants';
import { TableFooterNameType } from '../../../../Table/types/tableTypes';
import {
    TTableButtonsGetTranslation,
    TTableButtonsLogBusinessEvent,
    TUseTableFooterButtonsOnboarding,
    TUseTableFooterButtonsUrl,
} from '../useTableFooterButtons';

const useTableFooterButtonExpenses = (
    getTranslation: TTableButtonsGetTranslation,
    logBusinessEvent: TTableButtonsLogBusinessEvent,
    tableFooterName: TableFooterNameType,
) => {
    const { clientId } = useParams<IRouteParams>();

    return {
        className: 'table-footer__link link-balance',
        dataName: 'balance',
        icon: icons.balanceSmallIcon,
        id: AccessComponentKeyEnum.FooterButtonExpenses,
        ...mapTableFooterNameToOnboarding[tableFooterName],
        ...getTranslation(AccessComponentKeyEnum.FooterButtonExpenses),

        onClick: useCallback(() => {
            logBusinessEvent(ELogBusinessEventName.USER_SHOW_EXPENSES, ELogBusinessEventType.common);
            const urlConfig = mapTableFooterNameToUrl[tableFooterName];
            if (!urlConfig) return;

            appHistoryPush(urlConfig.url.replace(CLIENT_ID, clientId));
        }, [clientId, logBusinessEvent, tableFooterName]),
    };
};

const mapTableFooterNameToUrl: Partial<TUseTableFooterButtonsUrl> = {
    [TableFooterNameType.account]: {
        url: RoutePath.expensesAnalyzerAccount,
        external: ExternalRoutePath.expensesAnalyzerAccountBasket,
    },
    [TableFooterNameType.accountId]: {
        url: RoutePath.expensesAnalyzerAccountId,
        external: ExternalRoutePath.expensesAnalyzerAccountId,
    },
    [TableFooterNameType.subscriberId]: {
        url: RoutePath.expensesAnalyzerSubscriberMobileId,
        external: ExternalRoutePath.expensesAnalyzerSubscriberMobileBasket,
    },
    [TableFooterNameType.subscriberMobile]: {
        url: RoutePath.expensesAnalyzerSubscriberMobile,
        external: ExternalRoutePath.expensesAnalyzerSubscriberMobileBasket,
    },
};

const mapTableFooterNameToOnboarding: TUseTableFooterButtonsOnboarding = {
    [TableFooterNameType.account]: {
        onboarding: EOnboardingComponentsId.accountsTableFooterButtonExpenses,
    },
    [TableFooterNameType.subscriberMobile]: {
        onboarding: EOnboardingComponentsId.subscriberTableFooterButtonExpenses,
    },
};

export default useTableFooterButtonExpenses;
